/**
 * Results tab — the read-out of one finished case. OWNER: workstream F.
 *
 * Everything on this page is traceable to a file the pipeline wrote:
 * `<sid>_metrics.json`, `<sid>_automatic_radiological_report.txt`, the
 * `*_QFV.csv` / `*_lesionload.csv` tables and the reporting PNGs. Where the
 * mockup showed a value OpenADS does not compute (a model "Confidence"), the
 * card is replaced by segmentation Dice against ground truth and hidden
 * entirely when no ground truth was supplied — see the comments at each site.
 *
 * TODO(workstream C): register with the shell once `@/shell/tabRegistry` exists:
 *   registerTab("results", { centre: ResultsTab, right: ExportPanel });
 */

import * as React from "react";
import {
  Braces,
  Brain,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  Ruler,
  Target,
  Waves,
  X,
} from "lucide-react";
import { readTextFile } from "@/ipc/api";
import type { CaseManifest, ResultsSummary } from "@/ipc/contract";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Table,
  Td,
  Th,
} from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { openProtocolStep } from "@/pipeline/protocol";
import { protocolGates } from "@/quant/prerequisites";
import { openFilePath, refreshResults } from "@/results/actions";
import { selectTool } from "@/tools/actions";
import { appActions } from "@/store/appStore";
import { saveTextAsFile } from "@/results/clipboard";
import {
  ASPECTS_MAX,
  DASH,
  baseName,
  csvFileName,
  formatAspects,
  formatCount,
  formatMetric,
  formatMl,
  formatPercent,
  formatRatio,
  formatSeconds,
  toCsv,
  voxelCountMatchesMl,
} from "@/results/format";
import { useAssetUrl, useResultsStore, useResultsSummary } from "@/results/state";
import { MonoBlock, NoticeBar, SectionHeader, SourceNote } from "@/results/ui";

// ----------------------------------------------------------- metric cards ---

interface MetricCardModel {
  key: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  unit?: string;
  caption: string;
  /**
   * The same quantity in the patient's own space. The pipeline reports in
   * MNI152, where registration inflates a lesion by roughly half, so the
   * acquired-grid figure is shown beside the reported one rather than the UI
   * choosing between them.
   */
  native?: string;
}

function MetricCard({ card }: { card: MetricCardModel }) {
  return (
    <Card className="min-w-[168px] flex-1">
      <CardBody className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-neutral-400">{card.icon}</span>
          <span className="truncate text-xs font-medium text-neutral-600">{card.label}</span>
        </div>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-2xl font-semibold tabular-nums text-neutral-900">{card.value}</span>
          {card.unit ? <span className="text-xs text-neutral-500">{card.unit}</span> : null}
        </div>
        <p className="mt-1 text-[11px] leading-tight text-neutral-500">{card.caption}</p>
        {card.native ? (
          <p className="mt-0.5 text-[11px] leading-tight text-neutral-500">
            <span className="font-medium text-neutral-600">Native: </span>
            <span className="tabular-nums">{card.native}</span>
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * The "Native:" sub-line for a volume card. Omitted when the acquired-grid
 * figure is unavailable, and never shown alone — a lone number with no space
 * attached is what made the MNI volume read as the patient's in the first
 * place.
 */
function nativeLine(ml: number | null, vox: number | null): string | undefined {
  if (ml == null) return undefined;
  const mlText = `${formatMl(ml, 2)} mL`;
  return vox == null ? mlText : `${mlText} · ${formatCount(vox)} vox`;
}

function buildCards(summary: ResultsSummary): MetricCardModel[] {
  const cards: MetricCardModel[] = [];

  // Core volume. The mockup's "% of brain volume" sub-line is dropped: OpenADS
  // writes no brain-volume figure anywhere (the `brain` column of the lesion-load
  // tables is a lesion load, not a denominator), so the sub-line shows the voxel
  // count the millilitres came from instead — and only when the two agree.
  const mniVox = summary.dwiMetrics?.mni?.predVolumeVox ?? null;
  const coreCaption = voxelCountMatchesMl(mniVox, summary.coreVolumeMl)
    ? `${formatCount(mniVox)} voxels · MNI152 1 mm`
    : "From the radiological report";
  cards.push({
    key: "core",
    icon: <Brain className="h-4 w-4" />,
    label: "Stroke Core Volume",
    value: formatMl(summary.coreVolumeMl, 2),
    unit: "mL",
    caption: coreCaption,
    native: nativeLine(summary.coreVolumeNativeMl, summary.dwiMetrics?.orig?.predVolumeVox ?? null),
  });

  cards.push({
    key: "hypo",
    icon: <Waves className="h-4 w-4" />,
    label: "Hypoperfusion Volume",
    value: formatMl(summary.hypoperfusionVolumeMl, 2),
    unit: summary.hypoperfusionVolumeMl == null ? undefined : "mL",
    caption:
      summary.hypoperfusionVolumeMl == null
        ? "PWI pipeline not run for this case"
        : "TTP hypoperfusion, from the PWI report",
    native: nativeLine(
      summary.hypoperfusionVolumeNativeMl,
      summary.pwiMetrics?.orig?.predVolumeVox ?? null,
    ),
  });

  cards.push({
    key: "aspects",
    icon: <Ruler className="h-4 w-4" />,
    label: "ASPECTS",
    value: formatAspects(summary.aspects),
    unit: summary.aspects == null ? undefined : `/ ${ASPECTS_MAX}`,
    // Not an "Early CT score" as the mockup captioned it: this is the model's
    // predicted MCA-ASPECTS parsed out of the report text, on DWI, not CT.
    caption: "Predicted MCA–ASPECTS (report text)",
  });

  cards.push({
    key: "mismatch",
    icon: <Braces className="h-4 w-4" />,
    label: "Mismatch Ratio",
    value: formatRatio(summary.mismatchRatio),
    caption:
      summary.mismatchVolumeMl == null
        ? "Hypoperfusion / core — needs PWI"
        : `Hypoperfusion / core · ${formatMl(summary.mismatchVolumeMl, 2)} mL mismatch`,
    native:
      summary.mismatchRatioNative == null
        ? undefined
        : `${formatRatio(summary.mismatchRatioNative)} · ${formatMl(
            summary.mismatchVolumeNativeMl,
            2,
          )} mL mismatch`,
  });

  // The mockup's "Confidence 0.92" card is deliberately absent. OpenADS emits no
  // confidence score; what it does emit — when a ground-truth `*_stroke.nii.gz`
  // was supplied — is a Dice against that ground truth, so that is shown here and
  // the card disappears entirely when metrics.json has no ground-truth block.
  const dice = summary.dwiMetrics?.origPostproc?.dice ?? null;
  if (dice != null) {
    cards.push({
      key: "dice",
      icon: <Target className="h-4 w-4" />,
      label: "Dice vs ground truth",
      value: formatMetric(dice, 3),
      caption: "metrics.json · orig_postproc (native space)",
    });
  }

  cards.push({
    key: "runtime",
    icon: <Clock className="h-4 w-4" />,
    label: "Processing Time",
    value: formatSeconds(summary.processingSeconds),
    caption: "Total pipeline runtime",
  });

  return cards;
}

// ------------------------------------------------------- regional section ---

function sumOrNull(values: (number | null)[]): number | null {
  const present = values.filter((v): v is number => v != null && Number.isFinite(v));
  return present.length === 0 ? null : present.reduce((a, b) => a + b, 0);
}

function RegionalTable({ summary }: { summary: ResultsSummary }) {
  const rows = summary.regional;
  const hasHypo = rows.some((r) => r.hypoVolumeMl != null || r.hypoPct != null);

  const totals = React.useMemo(
    () => ({
      core: sumOrNull(rows.map((r) => r.coreVolumeMl)),
      corePct: sumOrNull(rows.map((r) => r.corePct)),
      hypo: sumOrNull(rows.map((r) => r.hypoVolumeMl)),
      hypoPct: sumOrNull(rows.map((r) => r.hypoPct)),
    }),
    [rows],
  );

  const onDownload = React.useCallback(async () => {
    const csv = toCsv(
      ["territory", "core_volume_ml", "core_pct", "hypoperfusion_volume_ml", "hypoperfusion_pct"],
      [
        ...rows.map((r) => [r.territory, r.coreVolumeMl, r.corePct, r.hypoVolumeMl, r.hypoPct]),
        ["Total (listed territories)", totals.core, totals.corePct, totals.hypo, totals.hypoPct],
      ],
    );
    const outcome = await saveTextAsFile(csvFileName(summary.subjectId, "regional_quantification"), csv);
    if (outcome !== "downloaded") {
      useResultsStore
        .getState()
        .setNotice(
          outcome === "copied"
            ? "The webview blocked the download, so the CSV is on your clipboard instead."
            : "Could not write the CSV. Use the Export panel to write the pipeline's own CSV tables.",
        );
    }
  }, [rows, summary.subjectId, totals]);

  return (
    <Card className="flex min-h-0 flex-col">
      <CardHeader>
        <CardTitle>Regional Quantification</CardTitle>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void onDownload()}
          disabled={rows.length === 0}
          title="Save the table as shown to a CSV file"
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
      </CardHeader>
      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <EmptyState
            title="No regional quantification"
            hint="The Reporting stage writes the QFV and lesion-load tables this view reads."
          />
        ) : (
          <Table>
            <thead className="sticky top-0 z-10 bg-white">
              <tr>
                <Th>Territory</Th>
                <Th className="text-right">Core Volume (mL)</Th>
                <Th className="text-right">Core %</Th>
                <Th className="text-right">
                  Hypoperfusion Volume (mL)
                  {hasHypo ? null : <span className="block font-normal text-neutral-400">PWI not run</span>}
                </Th>
                <Th className="text-right">
                  Hypoperfusion %
                  {hasHypo ? null : <span className="block font-normal text-neutral-400">PWI not run</span>}
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.territory} className="hover:bg-neutral-50">
                  <Td>{row.territory}</Td>
                  <Td className="text-right tabular-nums">{formatMl(row.coreVolumeMl)}</Td>
                  <Td className="text-right tabular-nums">{formatPercent(row.corePct)}</Td>
                  <Td className="text-right tabular-nums">{formatMl(row.hypoVolumeMl)}</Td>
                  <Td className="text-right tabular-nums">{formatPercent(row.hypoPct)}</Td>
                </tr>
              ))}
              <tr className="bg-neutral-50 font-medium">
                <Td>Total (listed territories)</Td>
                <Td className="text-right tabular-nums">{formatMl(totals.core)}</Td>
                <Td className="text-right tabular-nums">{formatPercent(totals.corePct)}</Td>
                <Td className="text-right tabular-nums">{formatMl(totals.hypo)}</Td>
                <Td className="text-right tabular-nums">{formatPercent(totals.hypoPct)}</Td>
              </tr>
            </tbody>
          </Table>
        )}
      </div>
      <div className="border-t border-neutral-200 px-4 py-2">
        <SourceNote>
          Rows come from the reporting stage's atlas tables. The total is the sum of the rows listed
          here, which covers the atlas' regions only — it need not equal the whole-lesion volume above.
        </SourceNote>
      </div>
    </Card>
  );
}

// --------------------------------------------------------- report preview ---

interface ReportEntry {
  label: string;
  path: string;
}

function ReportPreview({ summary }: { summary: ResultsSummary }) {
  const textReports = React.useMemo<ReportEntry[]>(() => {
    const fromList = summary.reports.filter((r) => r.kind === "txt").map((r) => ({ label: r.label, path: r.path }));
    if (fromList.length > 0) return fromList;
    // The summary always carries the primary report inline; keep a pager entry for
    // it even when the reports array is empty.
    return summary.radiologicalReportText ? [{ label: "Radiological Report", path: "" }] : [];
  }, [summary]);

  const pdfReports = React.useMemo(
    () => summary.reports.filter((r) => r.kind === "pdf"),
    [summary.reports],
  );

  const [index, setIndex] = React.useState(0);
  const [text, setText] = React.useState<string | null>(summary.radiologicalReportText);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => setIndex(0), [summary.caseId]);

  React.useEffect(() => {
    let cancelled = false;
    const entry = textReports[index];
    if (!entry) {
      setText(null);
      return;
    }
    if (index === 0 && summary.radiologicalReportText) {
      setText(summary.radiologicalReportText);
      setLoadError(null);
      return;
    }
    setText(null);
    setLoadError(null);
    readTextFile(entry.path, 512 * 1024)
      .then((r) => {
        if (cancelled) return;
        if (r.content) setText(r.content);
        else setLoadError(`Could not read ${baseName(entry.path)}.`);
      })
      .catch(() => {
        if (!cancelled) setLoadError(`Could not read ${baseName(entry.path)}.`);
      });
    return () => {
      cancelled = true;
    };
  }, [index, textReports, summary.radiologicalReportText]);

  const current = textReports[index];

  return (
    <Card className="flex min-h-0 flex-col">
      <CardHeader>
        <CardTitle>Report Preview</CardTitle>
        <div className="flex items-center gap-1">
          {current?.path ? (
            <Button size="sm" variant="ghost" onClick={() => void openFilePath(current.path)} title={current.path}>
              <ExternalLink className="h-3.5 w-3.5" />
              Open text
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <div className="min-h-0 flex-1 overflow-hidden p-3">
        {current ? (
          <>
            <div className="mb-2 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-neutral-400" />
              <span className="truncate text-xs font-medium text-neutral-700">{current.label}</span>
              {current.path ? (
                <span className="truncate text-[11px] text-neutral-400" title={current.path}>
                  {baseName(current.path)}
                </span>
              ) : null}
            </div>
            {text ? (
              // The report is fixed-width text with hand-aligned columns; it is
              // rendered verbatim and never reflowed.
              <MonoBlock text={text} className="h-[calc(100%-2rem)]" />
            ) : (
              <EmptyState
                title={loadError ?? "Loading report…"}
                hint={loadError ? "Open the file to inspect it in your text editor." : undefined}
              />
            )}
          </>
        ) : (
          <EmptyState
            title="No report for this case"
            hint="The Reporting stage writes *_automatic_radiological_report.txt."
          />
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-3 py-2">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            aria-label="Previous report"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs tabular-nums text-neutral-500">
            {textReports.length === 0 ? DASH : `${index + 1} / ${textReports.length}`}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={index >= textReports.length - 1}
            onClick={() => setIndex((i) => Math.min(textReports.length - 1, i + 1))}
            aria-label="Next report"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {pdfReports.map((pdf) => (
            <Button
              key={pdf.path}
              size="sm"
              onClick={() => void openFilePath(pdf.path)}
              // v1 has no embedded PDF renderer — say so rather than letting the
              // user wonder why nothing appears in the pane.
              title={`Opens ${baseName(pdf.path)} in your system PDF viewer — OpenADS Desktop v1 has no embedded PDF renderer.`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {pdf.label}
            </Button>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- figures ---

function FigureThumb({
  figure,
  onOpen,
}: {
  figure: { label: string; path: string };
  onOpen: (figure: { label: string; path: string }) => void;
}) {
  const url = useAssetUrl(figure.path);
  const [broken, setBroken] = React.useState(false);

  return (
    <div className="w-52 shrink-0">
      <button
        type="button"
        onClick={() => (url && !broken ? onOpen(figure) : void openFilePath(figure.path))}
        className={cn(
          "flex h-28 w-full items-center justify-center overflow-hidden rounded border border-neutral-200 bg-neutral-900/5",
          "transition-colors hover:border-blue-400",
        )}
        title={figure.path}
      >
        {url && !broken ? (
          <img
            src={url}
            alt={figure.label}
            className="h-full w-full object-contain"
            onError={() => setBroken(true)}
          />
        ) : (
          <span className="flex flex-col items-center gap-1 text-[11px] text-neutral-500">
            <ImageIcon className="h-4 w-4" />
            Open in system viewer
          </span>
        )}
      </button>
      <p className="mt-1 truncate text-[11px] text-neutral-600" title={figure.label}>
        {figure.label}
      </p>
      <p className="truncate text-[10px] text-neutral-400" title={figure.path}>
        {baseName(figure.path)}
      </p>
    </div>
  );
}

function FigureLightbox({
  figure,
  onClose,
}: {
  figure: { label: string; path: string };
  onClose: () => void;
}) {
  const url = useAssetUrl(figure.path);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-neutral-900/80 p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-3 pb-3 text-white">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{figure.label}</p>
          <p className="truncate text-[11px] text-neutral-300">{figure.path}</p>
        </div>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" onClick={() => void openFilePath(figure.path)}>
            <ExternalLink className="h-3.5 w-3.5" />
            Open in system viewer
          </Button>
          <Button size="sm" onClick={onClose} aria-label="Close">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center">
        {url ? (
          <img src={url} alt={figure.label} className="max-h-full max-w-full object-contain" />
        ) : (
          <p className="text-sm text-neutral-300">
            The figure could not be loaded in the app. Open it in your system viewer.
          </p>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- tab ---

export function ResultsTab() {
  const { summary, state, error, detail, reload } = useResultsSummary();
  const caseManifest = useResultsStore((s) => s.caseManifest);
  const notice = useResultsStore((s) => s.notice);
  const setNotice = useResultsStore((s) => s.setNotice);
  const [lightbox, setLightbox] = React.useState<{ label: string; path: string } | null>(null);

  if (!caseManifest) {
    return (
      <EmptyState
        title="No case is open"
        hint="Open a case from the File tab, or create one in the Pipeline tab."
      />
    );
  }

  if (state === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-neutral-700">{error}</p>
        {detail ? <MonoBlock text={detail} className="max-w-2xl" maxHeight={160} /> : null}
        <Button size="sm" onClick={reload}>
          <RefreshCw className="h-3.5 w-3.5" />
          Try again
        </Button>
      </div>
    );
  }

  if (state === "loading" || !summary) {
    return <EmptyState title={state === "loading" ? "Reading results…" : "No results loaded"} />;
  }

  const hasAnything =
    summary.coreVolumeMl != null ||
    summary.regional.length > 0 ||
    summary.reports.length > 0 ||
    summary.figures.length > 0 ||
    summary.radiologicalReportText != null;

  if (!hasAnything) {
    return (
      <div className="flex h-full flex-col">
        {notice ? (
          <div className="p-3">
            <NoticeBar message={notice} onDismiss={() => setNotice(null)} />
          </div>
        ) : null}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <EmptyState
            title={`No results for ${summary.subjectId} yet`}
            hint="Results is a cabinet of files the protocol has written. Measure a mask on Quantify immediately; ASPECTS / QFV / the report appear only after Reporting."
          />
          <div className="flex flex-wrap justify-center gap-1.5">
            <Button size="sm" variant="primary" onClick={() => appActions.setActiveTab("pipeline")}>
              Open protocol
            </Button>
            <Button
              size="sm"
              onClick={() => {
                selectTool("q.coreVolume");
              }}
            >
              Quantify
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const cards = buildCards(summary);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto bg-ads-bg">
      <div className="flex flex-col gap-3 p-3">
        <SectionHeader
          title="Results Summary"
          hint={`${summary.subjectId} · ${summary.pipelineKind.toUpperCase()} pipeline`}
          actions={
            <Button size="sm" onClick={reload} title="Re-read the output directory">
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          }
        />

        {notice ? <NoticeBar message={notice} onDismiss={() => setNotice(null)} /> : null}

        <ProtocolHonesty summary={summary} manifest={caseManifest} />

        {summary.warnings.length > 0 ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
            {summary.warnings.map((warning) => (
              <p key={warning} className="text-xs text-amber-900">
                {warning}
              </p>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {cards.map((card) => (
            <MetricCard key={card.key} card={card} />
          ))}
        </div>

        <div className="grid min-h-[26rem] grid-cols-1 gap-3 xl:grid-cols-2">
          <RegionalTable summary={summary} />
          <ReportPreview summary={summary} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Figures</CardTitle>
            <Badge tone="neutral">{summary.figures.length}</Badge>
          </CardHeader>
          <CardBody>
            {summary.figures.length === 0 ? (
              <SourceNote>
                No PNGs found. The Reporting stage writes `*_DWIstroke.png`, `*_DWIstroke_space-MNI152.png`
                and, for PWI, `*_HP.png`.
              </SourceNote>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {summary.figures.map((figure) => (
                  <FigureThumb key={figure.path} figure={figure} onOpen={setLightbox} />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {lightbox ? <FigureLightbox figure={lightbox} onClose={() => setLightbox(null)} /> : null}
    </div>
  );
}

function ProtocolHonesty({
  summary,
  manifest,
}: {
  summary: ResultsSummary;
  manifest: CaseManifest | null;
}) {
  const missing = protocolGates(manifest, summary).filter((g) => !g.ready);
  if (missing.length === 0) return null;
  return (
    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
      <p className="font-medium">Some protocol scores are not on disk yet — they are left blank, not estimated.</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {missing.map((gate) => (
          <li key={gate.id}>
            {gate.label}: {gate.missing}
            {gate.open ? (
              <>
                {" "}
                <button
                  type="button"
                  className="underline"
                  onClick={() => openProtocolStep(gate.open!.modality, gate.open!.id)}
                >
                  Open step
                </button>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Convenience for `registerTab("results", { centre: ResultsTab })`. */
export default ResultsTab;

/** Re-exported so the ribbon's Refresh button and this tab stay in step. */
export { refreshResults };
